// Системные уведомления устройства — тост Windows и запись в центре уведомлений (T-047).
//
// Технический слой без домена: что показать, решает лента (`modules/notifications/feed`).
// Только Windows-оболочка: Android WebView `Notification` не даёт, а браузерная версия
// кабинета — отдельное решение владельца, здесь она `'none'`.

import { desktopShellBridge, isDesktopShell } from './shell';

/**
 * Шаг 0 T-047 (`qa-reports/qa-report-t047-spike-windows-notifications-2026-09-30.md`):
 * оболочка без явного AUMID показывает HTML5-уведомление тостом, запись остаётся в центре
 * уведомлений под «Рестомаркер», `onclick` срабатывает и из центра. Значит, уведомления
 * работают на всех планшетах без переустановки установщика.
 */
export const LEGACY_SHELL_NOTIFICATIONS = true;

export type SystemNotificationSupport = 'ok' | 'needsShellUpdate' | 'none';

export function systemNotificationSupport(): SystemNotificationSupport {
  if (!isDesktopShell() || typeof window.Notification !== 'function') return 'none';
  if (desktopShellBridge()?.focusWindow) return 'ok';
  return LEGACY_SHELL_NOTIFICATIONS ? 'ok' : 'needsShellUpdate';
}

/** Спросить разрешение показывать уведомления. В Electron обычно сразу `granted`. */
export async function requestSystemNotificationPermission(): Promise<NotificationPermission> {
  if (typeof window.Notification !== 'function') return 'denied';
  if (window.Notification.permission !== 'default') return window.Notification.permission;
  return window.Notification.requestPermission();
}

export type SystemNotification = {
  readonly title: string;
  readonly body: string;
  /** Один `tag` — одна запись: повторный показ того же не двоит центр уведомлений. */
  readonly tag: string;
  /** После того как окно вышло вперёд. */
  readonly onClick?: () => void;
};

/**
 * Живые уведомления. Ссылка держится, иначе сборщик мусора уносит объект вместе
 * с `onclick`, и клик по записи в центре уведомлений ничего не делает. Держим немного:
 * старые записи всё равно уводят на тот же экран ленты.
 */
const alive = new Map<string, Notification>();
const ALIVE_LIMIT = 20;

export function showSystemNotification({ title, body, tag, onClick }: SystemNotification): void {
  const notification = new window.Notification(title, { body, tag });
  notification.onclick = () => {
    const focus = desktopShellBridge()?.focusWindow;
    if (focus) void focus();
    else window.focus();
    onClick?.();
  };
  alive.delete(tag);
  alive.set(tag, notification);
  while (alive.size > ALIVE_LIMIT) {
    const oldest = alive.keys().next().value;
    if (oldest === undefined) break;
    alive.delete(oldest);
  }
}
